<script lang="ts" setup>
import * as R from 'ramda'
import { computed, Ref, ref, watch } from 'vue'

import { BaseballIncidentId } from '@collector/sportsapi-client-legacy'
import { Incident } from '@collector/sportsapi-types'
import { useIncidents } from '@desktop/views/Relation/composables'
import { useBatterOptions } from '@desktop/views/Relation/Sports/Baseball/composables'
import { useEventIncidentsQueue } from '@desktop/views/Relation/Sports/composables'
import { IncidentPlayerOptionValue } from '@desktop/views/Relation/types'
import type { IncidentUpdate } from '@desktop/views/Relation/utils'

import PitchTitle from '../PitchTitle.vue'
import BallsStrikesOuts from './BallsStrikesOuts.vue'
import Base1Icon from './base1Icon.svg'
import Base2Icon from './base2Icon.svg'
import Base3Icon from './base3Icon.svg'
import BaseIcon from './BaseIcon.vue'
import PlayerBaseSelect from './PlayerBaseSelect.vue'
import {
  BaseballBaseIncidentId,
  getBaseIncidentIdFromOff,
  getBaseOffIncidentId,
} from './utils'

// composables
const { batterOptions } = useBatterOptions()
const eventIncidentsQueue = useEventIncidentsQueue()
const { incidentsUpdate } = useIncidents()

// state
const base1Player = ref<IncidentPlayerOptionValue>()
const base2Player = ref<IncidentPlayerOptionValue>()
const base3Player = ref<IncidentPlayerOptionValue>()

const baseIncidentIdToPlayerValueMap = new Map<
  BaseballBaseIncidentId,
  Ref<IncidentPlayerOptionValue | undefined>
>([
  [BaseballIncidentId.FirstBase, base1Player],
  [BaseballIncidentId.SecondBase, base2Player],
  [BaseballIncidentId.ThirdBase, base3Player],
])

// computed
const options = computed(() =>
  batterOptions.value.map((option) => {
    const { lineupId, ...rest } = option.value
    return { ...option, value: rest }
  }),
)

// methods
function findPlayerBaseIncidentId(
  playerValue: IncidentPlayerOptionValue,
): BaseballBaseIncidentId | undefined {
  for (const [
    baseIncidentId,
    basePlayerValue,
  ] of baseIncidentIdToPlayerValueMap.entries()) {
    if (R.equals(basePlayerValue.value, playerValue)) {
      return baseIncidentId
    }
  }

  return undefined
}

function addBaseIncident(
  incidentId: BaseballIncidentId,
  player: IncidentPlayerOptionValue,
): void {
  eventIncidentsQueue.addIncident({
    incident_id: incidentId,
    participant_team_id: Number(player.teamId),
    participant_id: player.id,
    participant_name: player.shortName,
  })
}

/**
 * Updates refs only: clears this player from any other base, then assigns
 * `playerValue` to `baseIncidentId`. Use when syncing from incidents (e.g. WebSocket).
 */
function setPlayerOnBase(
  baseIncidentId: BaseballBaseIncidentId,
  playerValue: IncidentPlayerOptionValue | undefined,
): void {
  const basePlayer = baseIncidentIdToPlayerValueMap.get(baseIncidentId)
  if (!basePlayer) return

  if (playerValue) {
    const previousBaseId = findPlayerBaseIncidentId(playerValue)
    if (previousBaseId) {
      const previousBasePlayer =
        baseIncidentIdToPlayerValueMap.get(previousBaseId)
      if (previousBasePlayer) previousBasePlayer.value = undefined
    }
  }

  basePlayer.value = playerValue ?? undefined
}

function sendOffIncidentForCurrentPlayerOnBase(
  baseIncidentId: BaseballBaseIncidentId,
): void {
  const basePlayer = baseIncidentIdToPlayerValueMap.get(baseIncidentId)
  if (basePlayer?.value) {
    addBaseIncident(getBaseOffIncidentId(baseIncidentId), basePlayer.value)
  }
}

function sendOffIncidentForPlayerFromBase(
  baseIncidentId: BaseballBaseIncidentId,
  playerValue: IncidentPlayerOptionValue,
): void {
  addBaseIncident(getBaseOffIncidentId(baseIncidentId), playerValue)
}

function onPlayerBaseChange([baseIncidentId, playerValue]: [
  BaseballBaseIncidentId,
  IncidentPlayerOptionValue,
]): void {
  sendOffIncidentForCurrentPlayerOnBase(baseIncidentId)

  if (!playerValue) {
    setPlayerOnBase(baseIncidentId, undefined)
    return
  }

  const previousBaseId = findPlayerBaseIncidentId(playerValue)
  if (previousBaseId) {
    sendOffIncidentForPlayerFromBase(previousBaseId, playerValue)
    setPlayerOnBase(previousBaseId, undefined)
  }

  addBaseIncident(baseIncidentId, playerValue)
  setPlayerOnBase(baseIncidentId, playerValue)
}

function isBaseOnIncidentId(
  incidentId: unknown,
): incidentId is BaseballBaseIncidentId {
  return baseIncidentIdToPlayerValueMap.has(
    incidentId as BaseballBaseIncidentId,
  )
}

function clearBaseForOffIncident(incidentId: BaseballIncidentId): void {
  const baseIncidentId = getBaseIncidentIdFromOff(incidentId)
  if (baseIncidentId) setPlayerOnBase(baseIncidentId, undefined)
}

function isBaseOffIncidentId(incidentId: BaseballIncidentId): boolean {
  return getBaseIncidentIdFromOff(incidentId) !== undefined
}

function getPlayerValueFromIncident(
  incident: Incident,
): IncidentPlayerOptionValue {
  return {
    id: incident.participant_id,
    shortName: incident.participant_name,
    teamId: incident.participant_team_id.toString(),
  }
}

function syncIncidentToBase(incident: Incident): void {
  const incidentId = incident.incident_id as BaseballIncidentId

  if (isBaseOnIncidentId(incidentId)) {
    setPlayerOnBase(incidentId, getPlayerValueFromIncident(incident))
    return
  }

  if (isBaseOffIncidentId(incidentId)) {
    clearBaseForOffIncident(incidentId)
  }
}

function syncBasesFromIncidentsUpdate(incidentsUpdate: IncidentUpdate[]): void {
  const incidents = incidentsUpdate.filter(
    ({ data }) => data.action !== 'delete',
  )

  for (const { data: incident } of incidents) {
    syncIncidentToBase(incident)
  }
}

// init
watch(incidentsUpdate, syncBasesFromIncidentsUpdate)
</script>

<template>
  <div>
    <PitchTitle>base runners</PitchTitle>
    <div class="flex flex-col gap-y-2">
      <PlayerBaseSelect
        v-model="base1Player"
        :options
        :baseIncidentId="BaseballIncidentId.FirstBase"
        @change="onPlayerBaseChange"
      >
        <BaseIcon :src="Base1Icon">1</BaseIcon>
      </PlayerBaseSelect>
      <PlayerBaseSelect
        v-model="base2Player"
        :options
        :baseIncidentId="BaseballIncidentId.SecondBase"
        @change="onPlayerBaseChange"
      >
        <BaseIcon :src="Base2Icon">2</BaseIcon>
      </PlayerBaseSelect>
      <PlayerBaseSelect
        v-model="base3Player"
        :options
        :baseIncidentId="BaseballIncidentId.ThirdBase"
        @change="onPlayerBaseChange"
      >
        <BaseIcon :src="Base3Icon">3</BaseIcon>
      </PlayerBaseSelect>
    </div>
    <BallsStrikesOuts />
  </div>
</template>
